// Light and dark appearance (GOD-61). The choice is per device and kept in
// localStorage; "auto" follows the system setting. The resolved scheme is set as
// data-bs-theme on <html>, which switches Bootstrap and the --gl-* tokens in
// theme.css together. Mails stay light – mail clients decide that themselves.

export type ColorSchemePreference = "auto" | "light" | "dark";
export type ColorScheme = "light" | "dark";

export const COLOR_SCHEME_STORAGE_KEY = "serva.colorScheme";
const PREFERENCES: ColorSchemePreference[] = ["auto", "light", "dark"];
// Browser chrome in dark mode (--gl-paper dark); light keeps the manifest colour.
const DARK_THEME_COLOR = "#161314";

export function isColorSchemePreference(value: unknown): value is ColorSchemePreference {
    return typeof value === "string" && (PREFERENCES as string[]).includes(value);
}

/** The stored choice; "auto" when nothing (valid) is stored or storage is blocked. */
export function readColorSchemePreference(): ColorSchemePreference {
    try {
        const stored = window.localStorage.getItem(COLOR_SCHEME_STORAGE_KEY);
        return isColorSchemePreference(stored) ? stored : "auto";
    } catch {
        return "auto";
    }
}

export function resolveColorScheme(preference: ColorSchemePreference, systemPrefersDark: boolean): ColorScheme {
    if (preference === "auto") return systemPrefersDark ? "dark" : "light";
    return preference;
}

/** Order of the sidebar button: auto → light → dark → auto. */
export function nextColorSchemePreference(preference: ColorSchemePreference): ColorSchemePreference {
    return PREFERENCES[(PREFERENCES.indexOf(preference) + 1) % PREFERENCES.length];
}

function darkQuery(): MediaQueryList | null {
    return typeof window !== "undefined" && typeof window.matchMedia === "function"
        ? window.matchMedia("(prefers-color-scheme: dark)")
        : null;
}

let preference: ColorSchemePreference = "auto";
let lightThemeColor: string | null = null;
const listeners = new Set<() => void>();

function currentScheme(): ColorScheme {
    return resolveColorScheme(preference, !!darkQuery()?.matches);
}

function apply(): void {
    const scheme = currentScheme();
    document.documentElement.setAttribute("data-bs-theme", scheme);
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
        if (lightThemeColor === null) lightThemeColor = meta.getAttribute("content");
        meta.setAttribute("content", scheme === "dark" ? DARK_THEME_COLOR : lightThemeColor ?? "");
    }
    listeners.forEach((listener) => listener());
}

/** Applies the stored choice before the first render and follows system changes. */
export function initColorScheme(): void {
    preference = readColorSchemePreference();
    apply();
    darkQuery()?.addEventListener("change", () => {
        if (preference === "auto") apply();
    });
}

export function setColorSchemePreference(next: ColorSchemePreference): void {
    preference = next;
    try {
        if (next === "auto") window.localStorage.removeItem(COLOR_SCHEME_STORAGE_KEY);
        else window.localStorage.setItem(COLOR_SCHEME_STORAGE_KEY, next);
    } catch {
        // Storage blocked (private mode): the choice lasts until the page is reloaded.
    }
    apply();
}

export function subscribeColorScheme(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

/** "<preference>:<scheme>" – a string, so useSyncExternalStore sees a stable value. */
export function getColorSchemeSnapshot(): string {
    return `${preference}:${currentScheme()}`;
}
